import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="imei-validator"
export default class extends Controller {
  static targets = ["feedback", "imeiInput"]
  
  connect() {
    this.lastCheckedImei = ''
    this.csrfToken = document.querySelector('meta[name="csrf-token"]').content
    this.typeApprovalsUrl = this.data.get("typeApprovalsUrl")
  }
  
  validate(event) {
    const value = event.target.value
    
    if (value.length === 0) {
      this.feedbackTarget.textContent = ''
      this.feedbackTarget.className = 'mt-1 text-[12px]'
      return
    }
    
    if (!/^\d+$/.test(value)) {
      this.feedbackTarget.textContent = 'IMEI must contain only digits'
      this.feedbackTarget.className = 'mt-1 text-[12px] text-red-600'
      return
    }
    
    if (value.length < 14) {
      this.feedbackTarget.textContent = `${14 - value.length} more digits required`
      this.feedbackTarget.className = 'mt-1 text-[12px] text-gray-500'
      return
    }
    
    if (value.length === 15 || value.length === 14) {
      this.feedbackTarget.textContent = 'Checking IMEI...'
      this.feedbackTarget.className = 'mt-1 text-[12px] text-blue-600'
      
      // Check IMEI and auto-populate if valid
      if (value !== this.lastCheckedImei) {
        this.lastCheckedImei = value
        this.checkImeiAndPopulate(value)
      }
    }
  }

  validate_length_only(event) {
        const value = event.target.value

        if (value.length === 0) {
            this.feedbackTarget.textContent = ''
            this.feedbackTarget.className = 'mt-1 text-[12px]'
            return
        }

        if (!/^\d+$/.test(value)) {
            this.feedbackTarget.textContent = 'IMEI must contain only digits'
            this.feedbackTarget.className = 'mt-1 text-[12px] text-red-600'
            return
        }

        if (value.length < 14) {
            this.feedbackTarget.textContent = `${14 - value.length} more digits required`
            this.feedbackTarget.className = 'mt-1 text-[12px] text-gray-500'
            return
        }

        if (value.length === 15 || value.length === 14) {
            this.feedbackTarget.textContent = ''
            this.feedbackTarget.className = 'mt-1 text-[12px]'
        }
    }
  
  async checkImeiAndPopulate(imei) {
    try {
      // Determine the correct endpoint based on current path
      const isPublic = window.location.pathname.includes('/public/') || window.location.pathname.includes('/simu_klear/')
      const endpoint = isPublic ? '/public/device_registrations/check_imei' : '/vendor/device_submissions/check_imei'
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ imei: imei })
      })
      
      const data = await response.json()
      
      if (data.valid && data.device_info) {
        // Check for TAC authorization warning
        if (data.warning) {
          this.feedbackTarget.innerHTML = `
            <span class="text-green-600">Valid IMEI - Device found in GSMA database</span><br>
            <span class="text-amber-600 font-medium">${data.warning}</span>
          `
          this.feedbackTarget.className = 'mt-1 text-[12px]'
          
          // Show warning notification
          this.showNotification(data.warning, 'warning')
        } else {
          this.feedbackTarget.textContent = 'Valid IMEI - Device found in GSMA database ✓'
          this.feedbackTarget.className = 'mt-1 text-[12px] text-green-600'
        }
        
        // Auto-populate device fields
        this.populateDeviceFields(data.device_info)

        // Fetch type approvals for the TAC if we're on vendor page
        if (this.typeApprovalsUrl && data.device_info.tac) {
          this.fetchTypeApprovals(data.device_info.tac)
        }
        
        // Show success notification only if no warning
        if (!data.warning) {
          this.showNotification('Device information auto-populated from GSMA database', 'success')
        }
      } else {
        this.feedbackTarget.textContent = data.error || 'Device not found in GSMA database'
        this.feedbackTarget.className = 'mt-1 text-[12px] text-red-600'
      }
    } catch (error) {
      this.feedbackTarget.textContent = 'Error checking IMEI'
      this.feedbackTarget.className = 'mt-1 text-[12px] text-red-600'
    }
  }
  
  populateDeviceFields(deviceInfo) {
    // Only populate if fields are empty
    const brandField = document.getElementById('device_registration_device_brand') || document.getElementById('device_request_device_brand')
    const modelField = document.getElementById('device_registration_device_model') || document.getElementById('device_request_device_model')
    const marketingNameField = document.getElementById('device_registration_marketing_name') || document.getElementById('device_request_marketing_name')
    const deviceTypeField = document.getElementById('device_registration_device_type') || document.getElementById('device_request_device_type')
    const secondaryImeiField = document.getElementById('device_registration_secondary_imei') || document.getElementById('device_request_other_imei')

    if (brandField && deviceInfo.brand) {
      brandField.value = deviceInfo.brand
      this.highlightField(brandField)
    }
    
    if (modelField && deviceInfo.model) {
      modelField.value = deviceInfo.model
      this.highlightField(modelField)
    }
    
    if (marketingNameField && deviceInfo.marketing_name) {
      marketingNameField.value = deviceInfo.marketing_name
      this.highlightField(marketingNameField)
    }
    
    if (deviceTypeField && deviceInfo.device_type) {
      deviceTypeField.value = deviceInfo.device_type
      this.highlightField(deviceTypeField)
    }

      if (secondaryImeiField) {
          const isRequired = deviceInfo.imei_quantity > 1;
          secondaryImeiField.required = isRequired;

          const secondaryImeiLabel = secondaryImeiField.previousElementSibling;
          if (secondaryImeiLabel) {
              // Remove any existing required markers to avoid duplication
              secondaryImeiLabel.querySelectorAll('span.text-red-600').forEach(el => el.remove());

              // Reset the label text cleanly
              secondaryImeiLabel.childNodes.forEach(node => {
                  if (node.nodeType === Node.TEXT_NODE) node.remove();
              });

              if (isRequired) {
                  secondaryImeiLabel.insertAdjacentText('afterbegin', 'Secondary IMEI ');
                  const requiredMarker = document.createElement('span');
                  requiredMarker.className = 'text-red-600';
                  requiredMarker.textContent = '*';
                  secondaryImeiLabel.appendChild(requiredMarker);
              } else {
                  secondaryImeiLabel.insertAdjacentText('afterbegin', 'Secondary IMEI (Optional)');
              }
          }
      }
  }
  
  highlightField(field) {
    field.classList.add('ring-2', 'ring-green-500', 'bg-green-50')
    setTimeout(() => {
      field.classList.remove('ring-2', 'ring-green-500', 'bg-green-50')
    }, 2000)
  }
  
  async fetchTypeApprovals(tac) {
    try {
      const response = await fetch(`${this.typeApprovalsUrl}?tac=${tac}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })
      
      const data = await response.json()
      
      if (data.success) {
        // Dispatch custom event with type approvals data
        const event = new CustomEvent('type-approvals-loaded', {
          detail: { typeApprovals: data.type_approvals, tac: tac },
          bubbles: true
        })
        this.element.dispatchEvent(event)
      }
    } catch (error) {
      console.error('Error fetching type approvals:', error)
    }
  }
  
  showNotification(message, type) {
    // For now, we'll keep the simple notification
    // In the future, this could be replaced with Turbo Streams to trigger Rails flash messages
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white z-50 ${
      type === 'success' ? 'bg-green-600' : 
      type === 'warning' ? 'bg-amber-600' : 
      'bg-red-600'
    }`
    notification.textContent = message
    document.body.appendChild(notification)
    
    setTimeout(() => {
      notification.remove()
    }, type === 'warning' ? 5000 : 3000)
  }
}
